import * as React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Menu from '@mui/material/Menu';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { alpha, useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import {
  getAuditLogsPagedByScope,
  getAuditLogDetail,
  type AuditLogsScope,
  type AuditLog,
  type AuditLogDetail,
} from '../../api/audits';
import {
  buildAuditExportFilename,
  buildAuditLogsCsv,
  downloadTextFile,
  enrichAuditLogsWithReasons,
  fetchAllAuditLogsForExport,
} from '../../utils/auditLogExport';
import { useAuth } from '../../auth/AuthContext';

interface AuditLogsLocationState {
  initialAction?: string;
  initialDate?: string;
  initialAgent?: string;
}

const APP_ROLE_PRIORITY = ['SUPPORT_AGENT', 'COMPLIANCE', 'OPERATIONS', 'FINANCE'] as const;

const AUDIT_SCOPE_LABELS: Record<AuditLogsScope, string> = {
  all: 'All Logs',
  transactions: 'Transactions Updates',
  contact: 'Client Lookup',
};

const AUDIT_ROLE_BANNER_BG = '#fffdec';
const AUDIT_ROLE_BANNER_BORDER = '#d4af37';
const AUDIT_ROLE_BANNER_TEXT = '#714b00';

function getPrimaryAppRole(realmRoles: string[]): string | null {
  return APP_ROLE_PRIORITY.find((role) => realmRoles.includes(role)) ?? null;
}

function getAllowedAuditScopes(realmRoles: string[]): AuditLogsScope[] {
  if (realmRoles.includes('SUPPORT_AGENT')) return ['all'];
  if (realmRoles.includes('COMPLIANCE')) return ['contact'];
  if (realmRoles.includes('OPERATIONS')) return ['all', 'transactions', 'contact'];
  return [];
}

function RoleBannerText({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      component="div"
      variant="body2"
      sx={{
        color: AUDIT_ROLE_BANNER_TEXT,
        fontSize: { xs: '0.875rem', sm: '0.9375rem' },
        lineHeight: 1.5,
        fontWeight: 500,
        '& strong': { fontWeight: 700 },
      }}
    >
      {children}
    </Typography>
  );
}

function AuditRoleInfoBanner({
  primaryRole,
  selectedScope,
}: {
  primaryRole: string | null;
  selectedScope: AuditLogsScope;
}) {
  if (primaryRole === 'SUPPORT_AGENT') return null;

  let content: React.ReactNode;
  if (primaryRole === 'COMPLIANCE') {
    content = (
      <RoleBannerText>
        You are in the Compliance role, viewing only Client Lookup audit logs.
      </RoleBannerText>
    );
  } else if (primaryRole === 'OPERATIONS') {
    content = (
      <RoleBannerText>
        You are in the Operations role, with access to all audit logs.
      </RoleBannerText>
    );
  } else {
    content = (
      <RoleBannerText>
        This view shows the audit log stream you are allowed to see for your account.
      </RoleBannerText>
    );
  }

  return (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        justifyContent: 'flex-start',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.5,
          width: 'fit-content',
          maxWidth: '100%',
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          borderRadius: '14px',
          border: `1px solid ${AUDIT_ROLE_BANNER_BORDER}`,
          bgcolor: AUDIT_ROLE_BANNER_BG,
          boxShadow: '0 1px 4px rgba(113, 75, 0, 0.12)',
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 26,
            height: 26,
            borderRadius: 1.5,
            bgcolor: 'primary.main',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography
            component="span"
            sx={{
              color: 'common.white',
              fontWeight: 800,
              fontSize: 14,
              lineHeight: 1,
              fontFamily: 'system-ui, sans-serif',
            }}
          >
            i
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>{content}</Box>
      </Box>
    </Box>
  );
}

type ActionPaletteKey = 'success' | 'info' | 'error' | 'warning';

const getActionPaletteKey = (action?: string): ActionPaletteKey => {
  switch (action?.toUpperCase()) {
    case 'ADDED':   return 'success';
    case 'UPDATED': return 'info';
    case 'DELETED': return 'error';
    case 'SWAPPED': return 'warning';
    default:        return 'info';
  }
};

const getActionIcon = (action?: string): React.ReactNode => {
  switch (action?.toUpperCase()) {
    case 'ADDED':   return <AddRoundedIcon sx={{ fontSize: 22 }} />;
    case 'UPDATED': return <EditRoundedIcon sx={{ fontSize: 22 }} />;
    case 'DELETED': return <DeleteRoundedIcon sx={{ fontSize: 22 }} />;
    case 'SWAPPED': return <SwapVertRoundedIcon sx={{ fontSize: 22 }} />;
    default:        return <InfoOutlinedIcon sx={{ fontSize: 22 }} />;
  }
};

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // silently fail
    }
  };

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
        {copied ? (
          <CheckCircleIcon sx={{ fontSize: 13, color: 'success.main' }} />
        ) : (
          <ContentCopyIcon sx={{ fontSize: 13 }} color="action" />
        )}
      </IconButton>
    </Tooltip>
  );
}

function DetailField({
  label,
  value,
  copyable = false,
}: {
  label: string;
  value?: string | null;
  copyable?: boolean;
}) {
  const displayValue = value?.trim() || '—';
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, display: 'block', mb: 0.25 }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
          {displayValue}
        </Typography>
        {copyable && displayValue !== '—' && <CopyButton value={displayValue} />}
      </Stack>
    </Box>
  );
}

type ActionColor = 'success' | 'info' | 'error' | 'warning' | 'default';

const actionChipColor = (action?: string): ActionColor => {
  switch (action?.toUpperCase()) {
    case 'ADDED':   return 'success';
    case 'UPDATED': return 'info';
    case 'DELETED': return 'error';
    case 'SWAPPED': return 'warning';
    default:        return 'default';
  }
};

const formatTimestamp = (raw?: string | null): string => {
  if (!raw) return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toLocalIsoDate = (raw?: string | null): string => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const matchesAuditLogFilters = (
  log: AuditLog,
  filters: {
    actionType: 'all' | 'SWAPPED' | 'ADDED' | 'UPDATED' | 'DELETED';
    startDate: string;
    endDate: string;
    clientIdOrEmail: string;
    agent: string;
  }
): boolean => {
  const clientTerm = filters.clientIdOrEmail.trim().toLowerCase();
  const agentTerm = filters.agent.trim().toLowerCase();
  const selectedAction = filters.actionType;
  const createdOnLocalDate = toLocalIsoDate(log.createdOn);

  const matchesDate =
    (!filters.startDate || createdOnLocalDate >= filters.startDate) &&
    (!filters.endDate || createdOnLocalDate <= filters.endDate);
  const matchesAction = selectedAction === 'all' || String(log.action ?? '').toUpperCase() === selectedAction;
  const matchesClient =
    !clientTerm ||
    String(log.externalClientId ?? '').toLowerCase().includes(clientTerm) ||
    String(log.entityId ?? '').toLowerCase().includes(clientTerm) ||
    String(log.clientFirstName ?? '').toLowerCase().includes(clientTerm) ||
    String(log.clientLastName ?? '').toLowerCase().includes(clientTerm);
  const matchesAgent = !agentTerm || String(log.createdBy ?? '').toLowerCase().includes(agentTerm);

  return matchesDate && matchesAction && matchesClient && matchesAgent;
};

type AuditLogsPageProps = {
  forcedScope?: AuditLogsScope;
  title?: string;
};

export default function AuditLogsPage({ forcedScope, title }: AuditLogsPageProps) {
  const PAGE_SIZE = 10;
  const theme = useTheme();
  const location = useLocation();
  const { realmRoles } = useAuth();
  const locationState = (location.state ?? {}) as AuditLogsLocationState;
  const primaryRole = React.useMemo(() => getPrimaryAppRole(realmRoles), [realmRoles]);
  const allowedScopes = React.useMemo(
    () => (forcedScope ? [forcedScope] : getAllowedAuditScopes(realmRoles)),
    [forcedScope, realmRoles]
  );
  const canSelectScope = allowedScopes.length > 1;

  const [clientIdOrEmail, setClientIdOrEmail] = React.useState('');
  const [actionType, setActionType] = React.useState<
    'all' | 'SWAPPED' | 'ADDED' | 'UPDATED' | 'DELETED'
  >(() => {
    const a = locationState.initialAction?.toUpperCase();
    if (a === 'SWAPPED' || a === 'ADDED' || a === 'UPDATED' || a === 'DELETED') return a;
    return 'all';
  });
  const [agent, setAgent] = React.useState(locationState.initialAgent ?? '');
  const [startDate, setStartDate] = React.useState(locationState.initialDate ?? '');
  const [endDate, setEndDate] = React.useState('');
  const [dateError, setDateError] = React.useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = React.useState<{
    action?: string;
    startDate?: string;
    endDate?: string;
    externalClientId?: string;
    createdBy?: string;
  }>(() => {
    const filters: { action?: string; startDate?: string; endDate?: string; externalClientId?: string; createdBy?: string } = {};
    const a = locationState.initialAction?.toUpperCase();
    if (a && a !== 'ALL') filters.action = a;
    if (locationState.initialDate) filters.startDate = locationState.initialDate;
    if (locationState.initialAgent?.trim()) filters.createdBy = locationState.initialAgent.trim();
    return filters;
  });
  const [currentPage, setCurrentPage] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [totalElements, setTotalElements] = React.useState(0);
  const [logs, setLogs] = React.useState<AuditLog[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedLogId, setSelectedLogId] = React.useState<number | null>(null);
  const [selectedLogDetail, setSelectedLogDetail] = React.useState<AuditLogDetail | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState<string | null>(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [downloadFetching, setDownloadFetching] = React.useState(false);
  const [downloadConfirmFormat, setDownloadConfirmFormat] = React.useState<'csv' | 'pdf' | null>(null);
  const [exportError, setExportError] = React.useState<string | null>(null);
  const [selectedScope, setSelectedScope] = React.useState<AuditLogsScope>(forcedScope ?? 'all');

  const hasExportFilters = React.useMemo(
    () =>
      Boolean(
        appliedFilters.action?.trim() ||
          appliedFilters.startDate?.trim() || appliedFilters.endDate?.trim() ||
          appliedFilters.externalClientId?.trim() ||
          appliedFilters.createdBy?.trim()
      ),
    [appliedFilters]
  );

  React.useEffect(() => {
    if (allowedScopes.length === 0) return;
    if (!allowedScopes.includes(selectedScope)) {
      setSelectedScope(allowedScopes[0]);
      setCurrentPage(0);
    }
  }, [allowedScopes, selectedScope]);

  const initiateDownload = (format: 'csv' | 'pdf') => {
    setExportError(null);
    if (!hasExportFilters) {
      setDownloadConfirmFormat(format);
      return;
    }
    void runDownload(format);
  };

  const runDownload = async (format: 'csv' | 'pdf') => {
    setDownloadConfirmFormat(null);
    setDownloadFetching(true);
    setExportError(null);
    try {
      const result = await fetchAllAuditLogsForExport({
        action: appliedFilters.action,
        startDate: appliedFilters.startDate,
        endDate: appliedFilters.endDate,
        externalClientId: appliedFilters.externalClientId,
        createdBy: appliedFilters.createdBy,
      }, selectedScope);
      if (!result.ok) {
        setExportError(result.message);
        return;
      }
      if (result.logs.length === 0) {
        setExportError('No audit logs to export for the current filters.');
        return;
      }
      const frontendFilteredLogs = result.logs.filter((log) =>
        matchesAuditLogFilters(log, { actionType, startDate, endDate, clientIdOrEmail, agent })
      );
      if (frontendFilteredLogs.length === 0) {
        setExportError('No audit logs to export for the current filters.');
        return;
      }
      const logsWithReason = await enrichAuditLogsWithReasons(frontendFilteredLogs);
      const filterKey = {
        action: appliedFilters.action,
        startDate: appliedFilters.startDate,
        endDate: appliedFilters.endDate,
        externalClientId: appliedFilters.externalClientId,
        createdBy: appliedFilters.createdBy,
      };
      if (format === 'csv') {
        downloadTextFile(
          buildAuditLogsCsv(logsWithReason),
          buildAuditExportFilename(filterKey, 'csv'),
          'text/csv;charset=utf-8'
        );
      } else {
        const { saveAuditLogsPdf } = await import('../../utils/auditLogPdf');
        saveAuditLogsPdf(logsWithReason, buildAuditExportFilename(filterKey, 'pdf'));
      }
    } catch (err) {
      console.error('Audit log export failed:', err);
      setExportError(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setDownloadFetching(false);
    }
  };

  React.useEffect(() => {
    let mounted = true;

    const loadLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getAuditLogsPagedByScope(selectedScope, {
          ...appliedFilters,
          page: currentPage,
          size: PAGE_SIZE,
        });

        if (!response.success) {
          if (!mounted) return;
          setError(response.message ?? 'Failed to fetch recent audit logs.');
          setLogs([]);
          setTotalElements(0);
          setTotalPages(0);
          return;
        }

        if (!mounted) return;
        setLogs(response.data?.logs ?? []);
        setCurrentPage(response.data?.page ?? currentPage);
        setTotalPages(response.data?.totalPages ?? 0);
        setTotalElements(response.data?.totalElements ?? 0);
      } catch (err) {
        if (!mounted) return;
        console.error('Failed to fetch audit logs:', err);
        setError('Unexpected error while fetching recent audit logs.');
        setLogs([]);
        setTotalElements(0);
        setTotalPages(0);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadLogs();

    return () => {
      mounted = false;
    };
  }, [appliedFilters, currentPage, PAGE_SIZE, selectedScope]);

  // Effect: inputs → update paginated server filters (debounced).
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const t = window.setTimeout(() => {
      if (startDate && endDate && startDate > endDate) {
        setDateError('Start date cannot be later than end date.');
        return;
      }
      setDateError(null);
      setAppliedFilters({
        action: actionType !== 'all' ? actionType : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        externalClientId: clientIdOrEmail.trim() || undefined,
        createdBy: agent.trim() || undefined,
      });
      setCurrentPage(0);
    }, 400);
    return () => window.clearTimeout(t);
  }, [actionType, startDate, endDate, clientIdOrEmail, agent]);

  const handleClearFilters = () => {
    setClientIdOrEmail('');
    setActionType('all');
    setAgent('');
    setStartDate('');
    setEndDate('');
    setDateError(null);
    setAppliedFilters({});
    setCurrentPage(0);
  };

  // ── Filtered view (current page only) ───────────────────────────────────────
  // Apply light client-side filtering on top of the current paginated page
  // so date / text filters always behave as expected visually.
  const filteredLogs = React.useMemo(() => {
    return logs.filter((log) =>
      matchesAuditLogFilters(log, { actionType, startDate, endDate, clientIdOrEmail, agent })
    );
  }, [logs, actionType, startDate, endDate, clientIdOrEmail, agent]);

  const handleRowClick = async (log: AuditLog) => {
    setSelectedLogId(log.id);
    setSelectedLogDetail(null);
    setDetailError(null);
    setDetailLoading(true);

    try {
      const response = await getAuditLogDetail(log.id);
      if (!response.success) {
        setDetailError(response.message ?? 'Failed to fetch audit log detail.');
        return;
      }
      setSelectedLogDetail(response.data ?? null);
    } catch (err) {
      console.error('Failed to fetch audit log detail:', err);
      setDetailError('Unexpected error while fetching audit log detail.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setSelectedLogId(null);
    setSelectedLogDetail(null);
    setDetailError(null);
  };

  return (
    <>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        {title ?? 'All Audit Logs'}
      </Typography>
      <AuditRoleInfoBanner primaryRole={primaryRole} selectedScope={selectedScope} />

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
          bgcolor: 'background.paper',
          transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: 3 },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            mb: 1.5,
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 700,
            color: 'primary.main',
          }}
        >
          Filter Logs
        </Typography>
        <Grid container spacing={2}>
          {canSelectScope && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Log Scope</InputLabel>
                <Select
                  label="Log Scope"
                  value={selectedScope}
                  onChange={(e) => {
                    setSelectedScope(e.target.value as AuditLogsScope);
                    setCurrentPage(0);
                  }}
                >
                  {allowedScopes.map((scope) => (
                    <MenuItem key={scope} value={scope}>
                      {AUDIT_SCOPE_LABELS[scope]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              size="small"
              fullWidth
              label="Client Email / ID"
              value={clientIdOrEmail}
              onChange={(e) => setClientIdOrEmail(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Action Type</InputLabel>
              <Select
                label="Action Type"
                value={actionType}
                onChange={(e) => setActionType(e.target.value as typeof actionType)}
              >
                <MenuItem value="all">All Actions</MenuItem>
                <MenuItem value="SWAPPED">Swap</MenuItem>
                <MenuItem value="ADDED">Add</MenuItem>
                <MenuItem value="UPDATED">Update</MenuItem>
                <MenuItem value="DELETED">Delete</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              size="small"
              fullWidth
              label="Agent"
              value={agent}
              onChange={(e) => setAgent(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              size="small"
              fullWidth
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              size="small"
              fullWidth
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>
        </Grid>
        {dateError && <Alert severity="warning" sx={{ mt: 1.5 }}>{dateError}</Alert>}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="text"
            size="small"
            onClick={handleClearFilters}
            disabled={loading && logs.length === 0}
          >
            Clear filters
          </Button>
        </Box>
        {loading && (
          <Box sx={{ mt: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {error && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
          bgcolor: 'background.paper',
          transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: 3 },
        }}
      >
        <Box
          sx={{
            mb: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              fontWeight: 700,
              color: 'primary.main',
            }}
          >
            Most Recent Logs
          </Typography>
          <Chip
            size="small"
            label={AUDIT_SCOPE_LABELS[selectedScope]}
            variant="outlined"
            color="primary"
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {downloadFetching && <CircularProgress size={18} />}
            <Button
              variant="outlined"
              size="small"
              endIcon={<KeyboardArrowDownIcon />}
              onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
              disabled={downloadFetching}
            >
              Download
            </Button>
            <Menu
              anchorEl={downloadMenuAnchor}
              open={Boolean(downloadMenuAnchor)}
              onClose={() => setDownloadMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => {
                  setDownloadMenuAnchor(null);
                  initiateDownload('csv');
                }}
                disabled={downloadFetching}
              >
                <TableChartOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
                CSV
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setDownloadMenuAnchor(null);
                  initiateDownload('pdf');
                }}
                disabled={downloadFetching}
              >
                <PictureAsPdfOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
                PDF
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        {exportError && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setExportError(null)}>
            {exportError}
          </Alert>
        )}
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>FXBO ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Client First Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Entity Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Agent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No audit logs to display. Adjust filters above.
                  </TableCell>
                </TableRow>
              )}
              {filteredLogs.map((log) => (
                <TableRow
                  key={log.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleRowClick(log)}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.75rem' }}>
                    {formatTimestamp(log.createdOn)}
                  </TableCell>
                  <TableCell>
                    {log.externalClientId ?? '—'}
                  </TableCell>
                  <TableCell>{log.clientFirstName ?? '—'}</TableCell>
                  <TableCell>{log.entityType ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={log.action ?? '—'}
                      size="small"
                      color={actionChipColor(log.action)}
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>{log.createdBy?.trim() || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">
            {totalElements > 0 ? `Showing page ${currentPage + 1} of ${totalPages}` : 'No results'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
              disabled={loading || currentPage <= 0}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setCurrentPage((prev) => prev + 1)}
              disabled={loading || totalPages === 0 || currentPage >= totalPages - 1}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={downloadConfirmFormat !== null}
        onClose={() => setDownloadConfirmFormat(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          No filters applied
          <IconButton size="small" onClick={() => setDownloadConfirmFormat(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have not applied any filters.
          </Alert>
          <DialogContentText>
            This will download <strong>all audit log records for {AUDIT_SCOPE_LABELS[selectedScope]}</strong>
            {' '}across every page, which may be a large file and could take a moment to fetch.
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadConfirmFormat(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (downloadConfirmFormat) void runDownload(downloadConfirmFormat);
            }}
          >
            Yes, download all
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Audit log detail dialog ─────────────────────────────────────────── */}
      <Dialog
        open={selectedLogId !== null}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, maxHeight: '92vh' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={700}>
              Audit Log Details
            </Typography>
            {selectedLogDetail && (
              <Chip
                label={`#${selectedLogDetail.id}`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
              />
            )}
          </Stack>
          <IconButton onClick={handleCloseDetail} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {!detailLoading && detailError && (
            <Box sx={{ p: 2.5 }}>
              <Alert severity="error">{detailError}</Alert>
            </Box>
          )}

          {!detailLoading && !detailError && selectedLogDetail && (() => {
            const paletteKey = getActionPaletteKey(selectedLogDetail.action);
            const actionColor = theme.palette[paletteKey].main;
            const clientName = [selectedLogDetail.clientFirstName, selectedLogDetail.clientLastName]
              .filter(Boolean).join(' ') || '—';

            return (
              <Stack spacing={0}>
                {/* ── Hero banner ── */}
                <Box
                  sx={{
                    px: 3,
                    py: 2.5,
                    background: theme.palette.mode === 'dark'
                      ? `linear-gradient(135deg, ${alpha(actionColor, 0.18)} 0%, ${alpha(actionColor, 0.06)} 100%)`
                      : `linear-gradient(135deg, ${alpha(actionColor, 0.10)} 0%, ${alpha(actionColor, 0.03)} 100%)`,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: actionColor,
                        flexShrink: 0,
                        fontWeight: 800,
                      }}
                    >
                      {getActionIcon(selectedLogDetail.action)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.25 }}>
                        <Typography variant="h6" fontWeight={800} noWrap>
                          {selectedLogDetail.entityType || '—'}
                        </Typography>
                        <Chip
                          label={selectedLogDetail.action}
                          size="small"
                          color={actionChipColor(selectedLogDetail.action)}
                          sx={{ fontWeight: 700, fontSize: '0.68rem', height: 22 }}
                        />
                      </Stack>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <AccessTimeRoundedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatTimestamp(selectedLogDetail.createdOn)}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </Box>

                {/* ── Cards ── */}
                <Box sx={{ px: 2.5, py: 2, overflow: 'auto' }}>
                  <Stack spacing={2}>

                    {/* Client Information */}
                    <Card
                      variant="outlined"
                      sx={{
                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                        transition: 'box-shadow 0.2s',
                        '&:hover': { boxShadow: 2 },
                      }}
                    >
                      <CardContent sx={{ pb: '16px !important' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                          <Box
                            sx={{
                              width: 26, height: 26, borderRadius: 1, display: 'flex',
                              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                            }}
                          >
                            <PersonRoundedIcon sx={{ fontSize: 15 }} />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, color: 'primary.main' }}
                          >
                            Client Information
                          </Typography>
                        </Stack>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={2}
                          divider={<Divider orientation="vertical" flexItem />}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                              <BadgeRoundedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                                External Client ID
                              </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="body2" fontWeight={600}>
                                {selectedLogDetail.externalClientId || '—'}
                              </Typography>
                              {selectedLogDetail.externalClientId && (
                                <CopyButton value={selectedLogDetail.externalClientId} />
                              )}
                            </Stack>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                              <PersonRoundedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                                Client Name
                              </Typography>
                            </Stack>
                            <Typography variant="body2" fontWeight={600}>
                              {clientName}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>

                    {/* Change Details */}
                    <Card
                      variant="outlined"
                      sx={{
                        borderLeft: `3px solid ${actionColor}`,
                        transition: 'box-shadow 0.2s',
                        '&:hover': { boxShadow: 2 },
                      }}
                    >
                      <CardContent sx={{ pb: '16px !important' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                          <Box
                            sx={{
                              width: 26, height: 26, borderRadius: 1, display: 'flex',
                              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                              bgcolor: alpha(actionColor, 0.1),
                              color: actionColor,
                            }}
                          >
                            {getActionIcon(selectedLogDetail.action)}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, color: actionColor }}
                          >
                            Change Details
                          </Typography>
                        </Stack>

                        <Stack spacing={1.5}>
                          {/* Before → After */}
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                          >
                            {/* Old Value */}
                            <Box
                              sx={{
                                flex: 1, p: 1.5, borderRadius: 1.5,
                                bgcolor: alpha(theme.palette.error.main, 0.06),
                                border: '1px solid',
                                borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.error.main, 0.3) : 'error.light',
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="error.main"
                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, display: 'block', mb: 0.5 }}
                              >
                                Before
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.8rem' }}
                              >
                                {selectedLogDetail.oldValue || '—'}
                              </Typography>
                            </Box>

                            {/* Arrow */}
                            <Box
                              sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                                py: { xs: 0, sm: 0 },
                                transform: { xs: 'rotate(90deg)', sm: 'none' },
                              }}
                            >
                              <ArrowForwardRoundedIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                            </Box>

                            {/* New Value */}
                            <Box
                              sx={{
                                flex: 1, p: 1.5, borderRadius: 1.5,
                                bgcolor: alpha(theme.palette.success.main, 0.06),
                                border: '1px solid',
                                borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.success.main, 0.3) : 'success.light',
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="success.main"
                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, display: 'block', mb: 0.5 }}
                              >
                                After
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.8rem' }}
                              >
                                {selectedLogDetail.newValue || '—'}
                              </Typography>
                            </Box>
                          </Stack>

                          <Divider />

                          {/* Reason */}
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, display: 'block', mb: 0.5 }}
                            >
                              Reason for Change
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {selectedLogDetail.reason || '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>

                    {/* Audit Trail */}
                    <Card
                      variant="outlined"
                      sx={{
                        borderLeft: `3px solid ${theme.palette.divider}`,
                        transition: 'box-shadow 0.2s',
                        '&:hover': { boxShadow: 2 },
                      }}
                    >
                      <CardContent sx={{ pb: '16px !important' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                          <Box
                            sx={{
                              width: 26, height: 26, borderRadius: 1, display: 'flex',
                              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                              bgcolor: 'action.hover',
                              color: 'text.secondary',
                            }}
                          >
                            <AccessTimeRoundedIcon sx={{ fontSize: 15 }} />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, color: 'text.secondary' }}
                          >
                            Audit Trail
                          </Typography>
                        </Stack>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={2}
                          divider={<Divider orientation="vertical" flexItem />}
                        >
                          <Stack spacing={1.5} sx={{ flex: 1 }}>
                            <DetailField label="Created By" value={selectedLogDetail.createdBy} />
                            <DetailField label="Created On" value={formatTimestamp(selectedLogDetail.createdOn)} />
                          </Stack>
                          <Stack spacing={1.5} sx={{ flex: 1 }}>
                            <DetailField label="Last Modified By" value={selectedLogDetail.lastModifiedBy} />
                            <DetailField label="Last Modified On" value={formatTimestamp(selectedLogDetail.lastModifiedOn)} />
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>

                  </Stack>
                </Box>
              </Stack>
            );
          })()}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleCloseDetail} variant="outlined" size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
